import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  type FreezeWindow,
  addDaysKey,
  freezeWindows,
  isGoalFrozen,
  thawWindows,
} from '@goaltree/backend/convex/lib/goalFreeze'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation } from 'convex/react'
import { Snowflake } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { keyToDate, todayKey } from '@/lib/day'
import { patchGoal } from '@/lib/goals-optimistic'

export function goalFreezeState(freezes: FreezeWindow[] | undefined) {
  const today = todayKey()
  const frozen = isGoalFrozen({ freezes }, today)
  return { frozen, until: frozen ? (freezes?.[freezes.length - 1]?.until ?? null) : null }
}

function useShortDate() {
  const { i18n } = useTranslation()
  return (key: string) =>
    new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric' }).format(
      keyToDate(key),
    )
}

function patchFreezes(
  store: OptimisticLocalStore,
  goalId: Id<'nodes'>,
  next: (prev: FreezeWindow[] | undefined, today: string) => FreezeWindow[],
) {
  const goal = store.getQuery(api.nodes.getUserGoals, {})?.find((g) => g._id === goalId)
  if (!goal) return
  const freezes = next(goal.freezes, todayKey())
  patchGoal(store, goalId, { freezes: freezes.length ? freezes : undefined })
}

export function useGoalFreezeActions(goalId: Id<'nodes'>) {
  const { t } = useTranslation()
  const short = useShortDate()
  const freeze = useMutation(api.nodes.freezeGoal).withOptimisticUpdate(
    (store, { goalId, until }) =>
      patchFreezes(store, goalId, (prev, today) => freezeWindows(prev, today, until)),
  )
  const unfreeze = useMutation(api.nodes.unfreezeGoal).withOptimisticUpdate((store, { goalId }) =>
    patchFreezes(store, goalId, thawWindows),
  )
  return {
    freeze: (until?: string) => {
      markChange(goalId, 'freeze')
      return freeze({ goalId, ...(until ? { until } : {}) })
        .then(() =>
          toast(
            until
              ? t('goalFreeze.frozenUntilToast', { date: short(until) })
              : t('goalFreeze.frozenToast'),
            {
              icon: <Snowflake className="size-4 text-sky-400" />,
            },
          ),
        )
        .catch(() => toast.error(t('common.error')))
    },
    unfreeze: () => {
      markChange(goalId, 'thaw')
      return unfreeze({ goalId })
        .then(() => toast.success(t('goalFreeze.unfrozenToast')))
        .catch(() => toast.error(t('common.error')))
    },
  }
}

const OPTIONS: { key: string; days?: number }[] = [
  { key: 'untilUnfreeze' },
  { key: 'oneWeek', days: 7 },
  { key: 'twoWeeks', days: 14 },
  { key: 'oneMonth', days: 30 },
]

export function FreezeGoalDialog({
  goalId,
  open,
  onOpenChange,
}: {
  goalId: Id<'nodes'>
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const short = useShortDate()
  const { freeze } = useGoalFreezeActions(goalId)
  const today = todayKey()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <div className="mx-auto mb-1 flex size-12 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-500 dark:text-sky-300">
            <Snowflake className="size-6" strokeWidth={2} />
          </div>
          <DialogTitle className="text-center">{t('goalFreeze.sheetTitle')}</DialogTitle>
          <p className="text-center text-sm text-muted-foreground">{t('goalFreeze.sheetDesc')}</p>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          {OPTIONS.map((o) => {
            // "1 week" thaws on day 8: the window is inclusive of its last day.
            const until = o.days ? addDaysKey(today, o.days - 1) : undefined
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => {
                  onOpenChange(false)
                  void freeze(until)
                }}
                className="flex cursor-pointer items-center justify-between rounded-xl border border-border/60 px-4 py-3 text-left text-sm font-semibold text-foreground transition-colors hover:border-sky-400/50 hover:bg-sky-400/10"
              >
                {t(`goalFreeze.${o.key}`)}
                {until && (
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('goalFreeze.thawsOn', { date: short(addDaysKey(until, 1)) })}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function FrozenPill({ until, className }: { until: string | null; className?: string }) {
  const { t } = useTranslation()
  const short = useShortDate()
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-sky-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm ring-1 ring-white/30 dark:bg-sky-500',
        className,
      )}
    >
      <Snowflake className="size-3" strokeWidth={2.4} />
      {until ? t('goalFreeze.frozenUntil', { date: short(until) }) : t('goalFreeze.frozen')}
    </span>
  )
}

// Freeze/thaw taps are recorded here so the next render of that goal's card can
// play the transition even though re-sorting remounts the card (a frozen goal
// moves to the end of its section).
const recentChange = new Map<string, { kind: 'freeze' | 'thaw'; at: number }>()
const TRANSITION_MS = 4000
function markChange(goalId: string, kind: 'freeze' | 'thaw') {
  recentChange.set(goalId, { kind, at: Date.now() })
}
function recent(goalId: string, kind: 'freeze' | 'thaw') {
  const c = recentChange.get(goalId)
  return !!c && c.kind === kind && Date.now() - c.at < TRANSITION_MS
}

type Phase = 'in' | 'static' | 'melt'

// A goal card's frost: animates in right after a freeze, melts away right after
// a thaw, and is simply present (no animation) otherwise.
export function GoalFrost({
  goalId,
  frozen,
  until,
  rounded = 'rounded-2xl',
  pillClassName,
}: {
  goalId: string
  frozen: boolean
  until: string | null
  rounded?: string
  pillClassName?: string
}) {
  const [melting, setMelting] = useState(() => !frozen && recent(goalId, 'thaw'))
  const wasFrozen = useRef(frozen)
  useEffect(() => {
    if (wasFrozen.current && !frozen) setMelting(true)
    wasFrozen.current = frozen
  }, [frozen])
  useEffect(() => {
    if (!melting) return
    const id = setTimeout(() => setMelting(false), 1600)
    return () => clearTimeout(id)
  }, [melting])
  const [phase] = useState<Phase>(() => (recent(goalId, 'freeze') ? 'in' : 'static'))
  if (frozen)
    return (
      <FrostOverlay
        key="frozen"
        phase={wasFrozen.current === frozen ? phase : 'in'}
        until={until}
        rounded={rounded}
        pillClassName={pillClassName}
      />
    )
  return melting ? (
    <FrostOverlay
      key="melt"
      phase="melt"
      until={null}
      rounded={rounded}
      pillClassName={pillClassName}
    />
  ) : null
}

const SPARKLES = [
  { top: '12%', left: '8%', size: 14, delay: '0s' },
  { top: '22%', right: '18%', size: 10, delay: '1.2s' },
  { bottom: '18%', left: '22%', size: 9, delay: '2.1s' },
  { bottom: '28%', right: '9%', size: 13, delay: '0.6s' },
  { top: '48%', left: '52%', size: 8, delay: '1.6s' },
]

const DROPS = [
  { left: '12%', h: 12, delay: 0 },
  { left: '31%', h: 16, delay: 0.12 },
  { left: '54%', h: 10, delay: 0.05 },
  { left: '72%', h: 14, delay: 0.18 },
  { left: '88%', h: 9, delay: 0.09 },
]

const EASE = [0.22, 1, 0.36, 1] as const

// Frosted glass over a frozen goal's card: icy tint, blur, rime edge, flakes.
function FrostOverlay({
  phase,
  until,
  rounded,
  pillClassName,
}: {
  phase: Phase
  until: string | null
  rounded: string
  pillClassName?: string
}) {
  const reduce = !!useReducedMotion()
  const animateIn = phase === 'in' && !reduce
  const melt = phase === 'melt'
  return (
    <motion.div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 z-10 overflow-hidden', rounded)}
      initial={animateIn ? { opacity: 0 } : melt ? { opacity: 1 } : false}
      animate={melt ? { opacity: 0, filter: reduce ? 'none' : 'blur(6px)' } : { opacity: 1 }}
      transition={
        melt
          ? { duration: reduce ? 0.2 : 1.3, delay: reduce ? 0 : 0.2, ease: 'easeIn' }
          : { duration: 0.8, ease: 'easeOut' }
      }
    >
      <div
        className={cn(
          'absolute inset-0 border-2 border-sky-200/80 bg-gradient-to-br from-sky-50/70 via-white/35 to-sky-200/55 backdrop-blur-[3px] backdrop-saturate-50 dark:border-sky-300/40 dark:from-sky-200/30 dark:via-sky-100/10 dark:to-sky-400/30',
          rounded,
        )}
      />
      <div
        className="absolute inset-0 opacity-70 mix-blend-overlay"
        style={{
          backgroundImage:
            'radial-gradient(circle at 15% 0%, rgba(255,255,255,0.95), transparent 42%), radial-gradient(circle at 100% 100%, rgba(186,230,253,0.9), transparent 48%)',
        }}
      />
      {animateIn && (
        <motion.div
          className="absolute inset-0 bg-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.35, 0] }}
          transition={{ duration: 0.7, times: [0, 0.25, 1] }}
        />
      )}
      {!melt &&
        SPARKLES.map(({ size, delay, ...pos }, i) => (
          <motion.span
            key={i}
            className="absolute"
            style={pos}
            initial={animateIn ? { opacity: 0, y: -18, rotate: -120, scale: 0.4 } : false}
            animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 + i * 0.09, ease: EASE }}
          >
            <Snowflake
              className="text-sky-400/80 motion-safe:animate-pulse dark:text-sky-200/80"
              style={{ width: size, height: size, animationDelay: delay, animationDuration: '3s' }}
              strokeWidth={2}
            />
          </motion.span>
        ))}
      {melt &&
        !reduce &&
        DROPS.map((d, i) => (
          <motion.span
            key={i}
            className="absolute top-0 block w-[3px] rounded-full bg-sky-300/90 dark:bg-sky-200/80"
            style={{ left: d.left, height: d.h }}
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: [0, 1, 0], y: [0, 50, 110] }}
            transition={{ duration: 1.1, delay: d.delay, ease: 'easeIn' }}
          />
        ))}
      {!melt && (
        <FrozenPill until={until} className={cn('absolute', pillClassName ?? 'top-2 right-2')} />
      )}
    </motion.div>
  )
}

export function FrozenBanner({ goalId, until }: { goalId: Id<'nodes'>; until: string | null }) {
  const { t } = useTranslation()
  const { unfreeze } = useGoalFreezeActions(goalId)
  return (
    <div className="flex items-center gap-3 rounded-xl border border-sky-300/50 bg-gradient-to-r from-sky-50 to-sky-100/60 px-3 py-2.5 dark:border-sky-300/25 dark:from-sky-400/10 dark:to-sky-300/5">
      <Snowflake className="size-4 shrink-0 text-sky-500 dark:text-sky-300" strokeWidth={2.2} />
      <p className="min-w-0 flex-1 text-xs font-medium text-sky-900 dark:text-sky-100">
        {t('goalFreeze.frozenBanner')}
        {until && <FrozenPill until={until} className="ml-1.5 align-middle" />}
      </p>
      <button
        type="button"
        onClick={() => void unfreeze()}
        className="shrink-0 cursor-pointer rounded-full bg-sky-500 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-sky-600"
      >
        {t('goalFreeze.unfreeze')}
      </button>
    </div>
  )
}
